/**
 * OCR taklifi (docs/13-hujjatlar.md 6.1): maydon | profilda | hujjatda | qabul. Hech narsa o'z-o'zidan yozilmaydi --
 * tanlanganlari "Qabul qilish" bilan driverga (versiya, audit, dublikat tekshiruvi backend'da). "Rad etish" -- faqat
 * ko'rib chiqilgan deb belgilanadi.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { DriverDetail } from "@/features/drivers/types";
import { useApplyOcr } from "@/features/documents/api";
import { defaultPicked, pickedValues, suggestionRows, type SuggestionField } from "@/features/documents/ocr";
import { kindLabel } from "@/features/documents/status";
import type { DriverDocument } from "@/features/documents/types";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

export function OcrSuggestionsModal({ doc, driver, onClose }: { doc: DriverDocument | null; driver: DriverDetail; onClose: () => void }) {
  const { t } = useTranslation("documents");
  const apply = useApplyOcr(driver.id);
  const ocr = doc?.ocr ?? null;
  const rows = useMemo(() => (ocr ? suggestionRows(ocr, driver) : []), [ocr, driver]);
  const [picked, setPicked] = useState<Set<SuggestionField>>(new Set());
  useEffect(() => setPicked(defaultPicked(rows)), [rows]);

  function submit(fields: Record<string, unknown>) {
    if (!doc) return;
    const n = Object.keys(fields).length;
    apply.mutate(
      { id: doc.id, fields, version: driver.version },
      {
        onSuccess: (res) => {
          toast.success(n ? t("ocr.applied", { count: res.applied.length }) : t("ocr.dismissed"), res.skipped.length ? t("ocr.skipped", { fields: res.skipped.map((f) => t(`fields.${f}`)).join(", ") }) : undefined);
          onClose();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.isConflict) {
            toast.warning(t("conflict"));
            onClose();
            return;
          }
          toast.error(err instanceof ApiError ? (err.fields ? Object.values(err.fields).flat().join(" ") : err.message) : t("failed"));
        },
      },
    );
  }

  const hasWarnings = !!ocr && (ocr.kind_mismatch || ocr.name_mismatch || (ocr.warnings?.length ?? 0) > 0);
  return (
    <Modal
      open={!!doc}
      onOpenChange={(o) => !o && onClose()}
      title={t("ocr.title")}
      description={ocr?.model ? t("ocr.modelLine", { model: ocr.model }) : undefined}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => submit({})} disabled={apply.isPending}>
            {doc?.ocr_reviewed_at ? t("ocr.close") : t("ocr.dismiss")}
          </Button>
          <Button variant="ink" loading={apply.isPending} disabled={picked.size === 0} onClick={() => ocr && submit(pickedValues(rows, picked, ocr))}>
            {t("ocr.accept", { count: picked.size })}
          </Button>
        </>
      }
    >
      {ocr && (
        <div className="space-y-3">
          {hasWarnings && (
            <ul className="space-y-1 rounded-xl bg-warning-soft px-4 py-3 text-[13px] text-warning">
              {ocr.kind_mismatch && <li>{t("ocr.kindMismatch", { kind: kindLabel(t, ocr.detected_kind ?? "", ocr.detected_kind ?? "") })}</li>}
              {ocr.name_mismatch && <li>{t("ocr.nameMismatch")}</li>}
              {ocr.warnings?.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
          {rows.length === 0 ? (
            <p className="text-[13px] text-muted">{t("ocr.nothing")}</p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] tracking-[0.06em] text-muted uppercase">
                  <th className="py-1 pr-3 text-left font-semibold">{t("ocr.cols.field")}</th>
                  <th className="py-1 pr-3 text-left font-semibold">{t("ocr.cols.profile")}</th>
                  <th className="py-1 pr-3 text-left font-semibold">{t("ocr.cols.document")}</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((r) => (
                  <tr key={r.field} className={cn(r.blocked && "text-muted")}>
                    <td className="py-2 pr-3 font-medium">
                      {t(`fields.${r.field}`)}
                      {r.blocked && <span className="ml-1 text-[11px] font-normal text-muted">· {t("ocr.sensitive")}</span>}
                    </td>
                    <td className="py-2 pr-3">{r.current || "—"}</td>
                    <td className={cn("py-2 pr-3 font-medium", r.low && "text-muted")}>
                      {r.suggested}
                      {r.low && <span className="ml-1 text-[11px] font-normal">· {t("ocr.lowConfidence")}</span>}
                      {!r.differs && <span className="ml-1 text-[11px] font-normal text-muted">· {t("ocr.same")}</span>}
                    </td>
                    <td className="py-2 text-right">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        disabled={r.blocked}
                        checked={picked.has(r.field)}
                        aria-label={t(`fields.${r.field}`)}
                        onChange={(e) =>
                          setPicked((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(r.field);
                            else next.delete(r.field);
                            return next;
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Modal>
  );
}
